import type { LooperEngine } from "../LooperEngine";
import type { Cell, Meter } from "../types";
import { newCell } from "../presets";
import { openNoteEditor } from "./editor";
import { makeDraggable, registerDropZone, type DragData } from "../../shared/dragDrop";
import { attachParamDrag } from "../../ui/paramDrag";
import type { ParamRef } from "../../ui/controls";

interface CellsOptions {
  onChange: () => void;
  /** A palette tile was dropped onto a box (by palette slot index). */
  onDropSlot?: (cellId: string, paletteIndex: number) => void;
  /** Register a palette slot's sound and return its id (for per-note sounds). */
  resolveDropSound?: (paletteIndex: number) => string | null;
  /** The "+ Song" button — append this box to the arrangement without dragging. */
  onAddToArrangement?: (cellId: string) => void;
}

// A drag-chip parameter (replaces the old horizontal range slider, which was
// nearly unusable on touch): press the chip and drag up/down to change; the
// shared HUD readout shows the value big while adjusting.
function knob(
  parent: HTMLElement,
  label: string,
  opts: { min: number; max: number; step: number; value: number; fmt: (v: number) => string },
  onInput: (v: number) => void,
): void {
  const chip = document.createElement("button");
  chip.type = "button";
  chip.className = "pknob";
  chip.title = `${label} — drag up/down`;
  const name = document.createElement("span");
  name.textContent = label;
  const val = document.createElement("b");
  val.textContent = opts.fmt(opts.value);
  chip.append(name, val);
  let cur = opts.value;
  const ref: ParamRef = {
    label,
    min: opts.min,
    max: opts.max,
    step: opts.step,
    scale: "lin",
    fmt: opts.fmt,
    get: () => cur,
    set: (v) => {
      cur = v;
      val.textContent = opts.fmt(v);
      onInput(v);
    },
  };
  attachParamDrag(chip, () => ref);
  parent.append(chip);
}

// Renders the boxes ("blobs"). Each box's hit timeline + playhead is redrawn
// every frame **while the transport runs**, so recording shows up live without
// rebuilds. Stopped, it draws once per interaction and then holds no animation
// frame at all — an idle page must be genuinely idle (phone battery, and a
// permanently busy page can't be screenshotted).
export class CellsView {
  private readonly canvases = new Map<string, HTMLCanvasElement>();
  private readonly boxEls = new Map<string, HTMLElement>();
  // Drop zones are re-registered every render(); track their teardown fns.
  private zoneCleanups: Array<() => void> = [];
  private raf = 0; // playback loop
  private syncRaf = 0; // pending one-shot redraw

  constructor(
    private readonly container: HTMLElement,
    private readonly engine: LooperEngine,
    private readonly opts: CellsOptions,
  ) {
    this.render();
    // Focus, hits, meter, bpm and the transport all only ever change off the
    // back of an interaction, so one frame after any of them keeps the boxes
    // in sync — and picks the playback loop up when the transport started.
    document.addEventListener("pointerdown", this.refresh, { passive: true });
    document.addEventListener("pointerup", this.refresh, { passive: true });
    document.addEventListener("keydown", this.refresh, { passive: true });
    document.addEventListener("keyup", this.refresh, { passive: true });
    document.addEventListener("visibilitychange", this.refresh);
    window.addEventListener("resize", this.refresh);
  }

  render(): void {
    for (const teardown of this.zoneCleanups) teardown();
    this.zoneCleanups = [];
    this.container.innerHTML = "";
    this.canvases.clear();
    this.boxEls.clear();

    for (const cell of this.engine.currentProject.cells) {
      this.container.append(this.renderCell(cell));
    }
    this.refresh(); // canvases have no size until the next frame
  }

  /** Redraw the boxes once on the next frame — the dirty flag for anything
   *  that changed a box's content (hits, meter, focus, bpm). Cheap and
   *  coalesced; also resumes the playhead loop if the transport is running. */
  refresh = (): void => {
    if (this.raf || this.syncRaf) return; // already drawing this frame
    this.syncRaf = requestAnimationFrame(() => {
      this.syncRaf = 0;
      this.draw();
      if (this.shouldAnimate()) this.raf = requestAnimationFrame(this.animate);
    });
  };

  /** Add a new box of the given meter (wired to the loops bar +buttons). */
  addCell(meter: Meter): void {
    this.engine.currentProject.cells.push(newCell(meter));
    this.opts.onChange();
    this.render();
  }

  private renderCell(cell: Cell): HTMLElement {
    const box = document.createElement("div");
    box.className = "box";
    // All boxes share the brand accent; their letter watermark + position are
    // the differentiating cues. (cell.color stays in the JSON for back-compat.)
    box.style.setProperty("--box-color", "#00bfff");
    this.boxEls.set(cell.id, box);

    const canvas = document.createElement("canvas");
    canvas.className = "box-timeline";
    canvas.title = "Click to edit notes (maximised)";
    this.canvases.set(cell.id, canvas);
    // Click the timeline → open the big maximised editor for this box.
    canvas.addEventListener("click", (e) => {
      e.stopPropagation();
      this.engine.focusCell(cell.id);
      openNoteEditor({ engine: this.engine, cell, onChange: this.opts.onChange, resolveDropSound: this.opts.resolveDropSound });
    });

    // Slim button row: grip · Mute · ✕. Recording goes through the top Record
    // button (tap the box to focus it first); the editor opens from the
    // timeline and holds Clear. Less chrome per box, same reach.
    const transport = document.createElement("div");
    transport.className = "box-buttons";

    const mute = document.createElement("button");
    mute.textContent = "Mute";
    mute.classList.toggle("on", cell.muted);
    mute.addEventListener("click", () => {
      cell.muted = !cell.muted;
      mute.classList.toggle("on", cell.muted);
      this.opts.onChange();
    });

    const del = document.createElement("button");
    del.textContent = "✕";
    del.title = "Delete box";
    del.addEventListener("click", (e) => {
      e.stopPropagation();
      const cells = this.engine.currentProject.cells;
      const i = cells.findIndex((c) => c.id === cell.id);
      if (i >= 0) cells.splice(i, 1);
      this.opts.onChange();
      this.render();
    });
    // Drag handle — pick the box up and drop it onto the arrangement as a chip.
    const dragData = (): DragData => {
      const idx = this.engine.currentProject.cells.indexOf(cell);
      const letter = idx >= 0 ? String.fromCharCode(65 + (idx % 26)) : "box";
      return { kind: "cell", id: cell.id, label: letter };
    };
    const grip = document.createElement("button");
    grip.className = "grip";
    grip.textContent = "⠿";
    grip.title = "Drag onto the arrangement";
    makeDraggable(grip, dragData);
    // The grip is ~30 px and sits right next to ✕, so the big timeline /
    // letter-watermark area is a second drag handle. A still press still opens
    // the editor — dragDrop only swallows the trailing click after a real drag.
    makeDraggable(canvas, dragData);

    // No-drag path: put this box straight at the end of the song.
    const addArr = document.createElement("button");
    addArr.className = "arr-add";
    addArr.textContent = "+ Song";
    addArr.title = "Add this box to the end of the arrangement";
    addArr.addEventListener("click", (e) => {
      e.stopPropagation();
      this.opts.onAddToArrangement?.(cell.id);
    });

    transport.append(grip, addArr, mute, del);

    const knobs = document.createElement("div");
    knobs.className = "box-knobs";
    knob(knobs, "Pitch", { min: -24, max: 24, step: 1, value: cell.transpose, fmt: (v) => `${v > 0 ? "+" : ""}${v}` }, (v) => {
      cell.transpose = v;
      this.opts.onChange();
    });
    knob(knobs, "Speed", { min: 0.25, max: 4, step: 0.05, value: cell.speed, fmt: (v) => `${v.toFixed(2)}×` }, (v) => {
      cell.speed = v;
      this.opts.onChange();
    });
    knob(knobs, "Level", { min: 0, max: 1, step: 0.01, value: cell.gain, fmt: (v) => v.toFixed(2) }, (v) => {
      cell.gain = v;
      this.opts.onChange();
    });

    box.append(canvas, transport, knobs);
    // Click anywhere on the box to focus it (so the R key records into it).
    box.addEventListener("click", () => this.engine.focusCell(cell.id));

    // Accept palette tiles dropped onto the box (phrase → fill, sound → re-skin).
    this.zoneCleanups.push(
      registerDropZone({
        el: box,
        overClass: "drop",
        accepts: (d) => d.kind === "slot",
        onDrop: (d) => this.opts.onDropSlot?.(cell.id, Number(d.id)),
      }),
    );

    return box;
  }

  private shouldAnimate(): boolean {
    return this.engine.playing && !document.hidden;
  }

  private animate = (): void => {
    this.draw();
    // Stopped? Stop looping. The draw above already cleared the playhead —
    // getCellPhase() returns 0 once the transport is off.
    this.raf = this.shouldAnimate() ? requestAnimationFrame(this.animate) : 0;
  };

  private draw(): void {
    const recId = this.engine.recordCellId;
    for (const [id, el] of this.boxEls) {
      el.classList.toggle("focused", id === recId);
    }
    for (const cell of this.engine.currentProject.cells) {
      const canvas = this.canvases.get(cell.id);
      if (canvas) this.drawTimeline(canvas, cell);
    }
  }

  private drawTimeline(canvas: HTMLCanvasElement, cell: Cell): void {
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    if (canvas.width !== Math.floor(w * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Background letter A,B,C… by position + small meter tag in the corner.
    const idx = this.engine.currentProject.cells.indexOf(cell);
    if (idx >= 0) {
      const letter = String.fromCharCode(65 + (idx % 26));
      ctx.save();
      ctx.fillStyle = "rgba(255,255,255,0.13)";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `800 ${Math.floor(h * 0.78)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.fillText(letter, w * 0.5, h * 0.5);
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.font = `600 10px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textAlign = "right";
      ctx.fillText(`${letter} · ${cell.meter}`, w - 8, 12);
      ctx.restore();
    }

    // ---- Grid + loop frame (a buffer after the loop shows tails that ring over) ----
    const lb = cell.lengthBeats;
    const vb = lb + Math.min(4, Math.max(1, Math.round(lb * 0.15)));
    const beat = 60 / this.engine.currentProject.bpm;
    const loopPre = lb * beat;
    const visibleDur = vb * beat;
    const frameX = (lb / vb) * w;

    // Buffer zone tint.
    ctx.fillStyle = "rgba(0,0,0,0.22)";
    ctx.fillRect(frameX, 0, w - frameX, h);

    // Beat grid — brighter than before; faint in the buffer.
    ctx.lineWidth = 1;
    for (let b = 0; b <= vb; b++) {
      ctx.strokeStyle = b <= lb ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.07)";
      const x = (b / vb) * w;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    // Loop-end marker.
    ctx.strokeStyle = "rgba(0,191,255,0.7)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(frameX, 0);
    ctx.lineTo(frameX, h);
    ctx.stroke();
    ctx.lineWidth = 1;

    // Hits as little bars so length — and any tail past the loop — is visible.
    ctx.fillStyle = "#00bfff";
    for (const hit of cell.hits) {
      const x = visibleDur > 0 ? (hit.time / visibleDur) * w : 0;
      const bw = Math.max(3, (hit.dur / visibleDur) * w);
      const pitch = Math.min(1, Math.max(0, (hit.midi - 36) / 60));
      const y = h - pitch * (h - 8) - 4;
      ctx.globalAlpha = 0.5 + hit.gain * 0.5;
      ctx.fillRect(x, y - 2, bw, 4);
      const overflow = hit.time + hit.dur - loopPre;
      if (overflow > 0) {
        ctx.globalAlpha = 0.25;
        ctx.fillRect(0, y - 2, Math.min((overflow / visibleDur) * w, frameX), 4);
      }
    }
    ctx.globalAlpha = 1;

    // Playhead — sweeps the loop frame only.
    const phase = this.engine.getCellPhase(cell);
    if (phase > 0) {
      ctx.strokeStyle = "#fff";
      ctx.beginPath();
      ctx.moveTo(phase * frameX, 0);
      ctx.lineTo(phase * frameX, h);
      ctx.stroke();
    }
  }
}
